import React, { useEffect, useMemo, useState } from "react";
import { Globe2 } from "lucide-react";
import OnlineTeacherCard from "./OnlineTeacherCard";
import { getAvailableNeedChips } from "../../lib/onlineTeacherCardModel";
import {
  describeTimeZone,
  detectViewerTimeZone,
  localizeScheduleEntries,
  SOURCE_TIME_ZONE,
  SOURCE_TIME_ZONE_LABEL,
  TIME_PERIOD_CHIPS,
  teacherMatchesTimePeriod,
} from "../../lib/localSchedule";

export default function TeacherDiscoverySection({
  teachers,
  loading,
  activeNeed,
  onNeedChange,
  onBookConsultation,
  onOpenConsultation,
}) {
  // Resolved after mount so the prerendered HTML (which has no viewer
  // timezone) stays identical to the first client render.
  const [viewerTimeZone, setViewerTimeZone] = useState("");

  useEffect(() => {
    setViewerTimeZone(detectViewerTimeZone());
  }, []);

  const [activePeriod, setActivePeriod] = useState(null);

  const chips = getAvailableNeedChips(teachers);
  const zoneForSlots = viewerTimeZone || SOURCE_TIME_ZONE;

  const teachersWithSlots = useMemo(
    () =>
      teachers.map((teacher) => ({
        ...teacher,
        localSlots: localizeScheduleEntries(teacher.scheduleEntries, zoneForSlots),
      })),
    [teachers, zoneForSlots]
  );

  const availablePeriods = useMemo(() => {
    const present = new Set();
    for (const teacher of teachersWithSlots) {
      for (const slot of teacher.localSlots || []) {
        if (slot.period) present.add(slot.period);
      }
    }
    return TIME_PERIOD_CHIPS.filter((chip) => present.has(chip.id));
  }, [teachersWithSlots]);

  const filtered = teachersWithSlots.filter((teacher) => {
    if (activeNeed && !(teacher.needTags || []).includes(activeNeed)) return false;
    if (!teacherMatchesTimePeriod(teacher.localSlots, activePeriod)) return false;
    return true;
  });

  return (
    <section id="teachers" className="scroll-mt-28 bg-gray-50/80" aria-labelledby="teachers-heading">
      <div className="container mx-auto px-4 py-16 md:py-20">
        <div className="mx-auto max-w-3xl text-center">
          <h2
            id="teachers-heading"
            className="text-3xl font-black tracking-tight text-gray-900 md:text-4xl"
          >
            Meet Our Online Yoga Teachers
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-gray-600">
            Find a teacher who matches your goals, experience and yoga style. Live class times are
            converted to{" "}
            <span className="font-semibold text-gray-800">
              {viewerTimeZone ? `your timezone (${describeTimeZone(viewerTimeZone)})` : "your timezone"}
            </span>
            , so you can see what actually works from where you are.
          </p>

          <p className="mt-5 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 ring-1 ring-emerald-100">
            <Globe2 size={15} strokeWidth={2.5} />
            {viewerTimeZone
              ? `Showing class times in ${describeTimeZone(viewerTimeZone)}`
              : `Class times shown in the teacher's timezone (${SOURCE_TIME_ZONE_LABEL}) until we detect yours`}
          </p>
        </div>

        {chips.length > 0 ? (
          <div className="mx-auto mt-10 max-w-4xl" id="find-by-need">
            <p className="text-center text-sm font-semibold uppercase tracking-[0.12em] text-gray-500">
              I&apos;m looking for…
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <button
                type="button"
                onClick={() => onNeedChange(null)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  !activeNeed
                    ? "bg-emerald-600 text-white"
                    : "bg-white text-gray-700 ring-1 ring-gray-200 hover:ring-emerald-300"
                }`}
              >
                All teachers
              </button>
              {chips.map((chip) => (
                <button
                  key={chip.id}
                  type="button"
                  onClick={() => onNeedChange(chip.id === activeNeed ? null : chip.id)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    activeNeed === chip.id
                      ? "bg-emerald-600 text-white"
                      : "bg-white text-gray-700 ring-1 ring-gray-200 hover:ring-emerald-300"
                  }`}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {availablePeriods.length > 0 ? (
          <div className="mx-auto mt-8 max-w-4xl">
            <p className="text-center text-sm font-semibold uppercase tracking-[0.12em] text-gray-500">
              Classes that fit my day
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <button
                type="button"
                onClick={() => setActivePeriod(null)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  !activePeriod
                    ? "bg-teal-700 text-white"
                    : "bg-white text-gray-700 ring-1 ring-gray-200 hover:ring-teal-300"
                }`}
              >
                Any time
              </button>
              {availablePeriods.map((chip) => (
                <button
                  key={chip.id}
                  type="button"
                  onClick={() => setActivePeriod(chip.id === activePeriod ? null : chip.id)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    activePeriod === chip.id
                      ? "bg-teal-700 text-white"
                      : "bg-white text-gray-700 ring-1 ring-gray-200 hover:ring-teal-300"
                  }`}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {loading ? (
          <div className="mt-12 flex justify-center py-16">
            <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-emerald-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="mx-auto mt-12 max-w-lg rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm">
            <p className="text-gray-700">
              No teachers match that filter right now. Try another time of day, or book a free
              consultation and we&apos;ll help you find a good fit.
            </p>
            <button
              type="button"
              onClick={onOpenConsultation}
              className="mt-5 inline-flex items-center justify-center rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"
            >
              Book My Free Consultation
            </button>
          </div>
        ) : (
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
            {filtered.map((teacher) => (
              <OnlineTeacherCard
                key={teacher.slug}
                teacher={teacher}
                onBookConsultation={onBookConsultation}
                viewerTimeZone={viewerTimeZone}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
